/**
 * Spare-parts inventory for the panels: public/data/inventory.json through
 * react-query and the analysis InventoryManager for lookups and the
 * in-memory reservations (the manager keeps the mutable quantities; a
 * version counter here re-renders the panels after a reservation).
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { createInventoryManager, type InventoryData, type InventoryManager, type InventoryPart, type InventoryStatus, type Reservation } from '@/features/twin/analysis';
import { clockNow } from '@/state/store';
import { useJsonFile } from './dataFiles';

export const INVENTORY_URL = '/data/inventory.json';

export type { InventoryData as InventoryFile, InventoryManager, InventoryPart, InventoryStatus, Reservation };

export function useInventory() {
  return useJsonFile<InventoryData>(INVENTORY_URL);
}

export function allParts(inv: InventoryData | undefined): InventoryPart[] {
  return inv ? Object.values(inv.parts) : [];
}

export function getPart(inv: InventoryData | undefined, partNumber: string | undefined): InventoryPart | null {
  if (!inv || !partNumber) return null;
  return inv.parts[partNumber] ?? null;
}

/** Parts for an equipment key (cooling_fan, lna, ...), file order; the first is the primary spare. */
export function partsByEquipment(inv: InventoryData | undefined, equipment: string): InventoryPart[] {
  return allParts(inv).filter((p) => p.equipment === equipment);
}

/**
 * Equipment key for a subsystem id. The previous keyword rule (fan/cooling,
 * lna, hpa/sspa, antenna/az/el, modem, switch/network) left the LNA, drive
 * system and waveguide without a badge; the extra keywords map those onto
 * the parts inventory.json already holds for them.
 */
export function equipmentKeyFor(subsystemId: string): string | null {
  const id = subsystemId.toLowerCase();
  const has = (...keys: string[]) => keys.some((k) => id.includes(k));
  if (has('fan', 'cooling')) return 'cooling_fan';
  if (has('lna', 'low_noise')) return 'lna';
  if (has('hpa', 'sspa')) return 'hpa';
  if (has('antenna', 'drive', 'motor', 'bearing', 'pedestal', '_az', 'az_', '_el', 'el_')) return 'antenna';
  if (has('modem')) return 'modem';
  if (has('switch', 'network')) return 'network_switch';
  if (has('waveguide')) return 'waveguide';
  if (has('freq', 'translator')) return 'freq_translator';
  if (has('power', 'psu')) return 'power_supply';
  return null;
}

/** The primary spare for a subsystem from the file, or null when no keyword matches. */
export function inventoryBySubsystemId(inv: InventoryData | undefined, subsystemId: string): InventoryPart | null {
  const key = equipmentKeyFor(subsystemId);
  if (!key) return null;
  return partsByEquipment(inv, key)[0] ?? null;
}

interface ManagerState {
  manager: InventoryManager | null;
  /** The data object the manager was built from (react-query hands back the same reference while cached). */
  source: InventoryData | null;
  version: number;
  ensure: (data: InventoryData) => void;
  reserve: (partNumber: string, reason: string) => Reservation | null;
  release: (id: string, used: boolean) => boolean;
}

export const useInventoryManagerStore = create<ManagerState>()((set, get) => ({
  manager: null,
  source: null,
  version: 0,
  ensure: (data) => {
    if (get().source === data) return;
    set((s) => ({ manager: createInventoryManager(data, { nowMs: clockNow, random: Math.random }), source: data, version: s.version + 1 }));
  },
  reserve: (partNumber, reason) => {
    const r = get().manager?.reservePart(partNumber, reason) ?? null;
    if (r) set((s) => ({ version: s.version + 1 }));
    return r;
  },
  release: (id, used) => {
    const ok = get().manager?.releaseReservation(id, used) ?? false;
    if (ok) set((s) => ({ version: s.version + 1 }));
    return ok;
  },
}));

export interface InventoryView {
  data: InventoryData | undefined;
  manager: InventoryManager | null;
  version: number;
  error: Error | null;
  /** Current parts (quantities after reservations), file order. */
  parts: InventoryPart[];
  reservations: Reservation[];
  partFor: (partNumber: string | undefined) => InventoryPart | null;
  /** Primary spare for a subsystem id with the current quantity. */
  bySubsystem: (subsystemId: string) => InventoryStatus | null;
  reserve: (partNumber: string, reason: string) => Reservation | null;
  release: (id: string, used: boolean) => boolean;
}

/** The inventory as the panels use it, backed by one manager per loaded file. */
export function useInventoryView(): InventoryView {
  const query = useInventory();
  const data = query.data;
  const manager = useInventoryManagerStore((s) => s.manager);
  const source = useInventoryManagerStore((s) => s.source);
  const version = useInventoryManagerStore((s) => s.version);
  const reserve = useInventoryManagerStore((s) => s.reserve);
  const release = useInventoryManagerStore((s) => s.release);
  useEffect(() => {
    if (data && source !== data) useInventoryManagerStore.getState().ensure(data);
  }, [data, source]);
  const ready = data !== undefined && source === data ? manager : null;
  const parts = data ? Object.keys(data.parts).map((pn) => ready?.getPart(pn) ?? data.parts[pn]) : [];
  return {
    data,
    manager: ready,
    version,
    error: query.isError ? query.error : null,
    parts,
    reservations: ready?.getActiveReservations() ?? [],
    partFor: (pn) => (pn ? (ready?.getPart(pn) ?? getPart(data, pn)) : null),
    bySubsystem: (subsystemId) => {
      if (!ready) return null;
      const key = equipmentKeyFor(subsystemId);
      return key ? ready.getInventoryForEquipment(key) : null;
    },
    reserve,
    release,
  };
}
